/**
 * Cotton Cubs — Official Website
 * ─────────────────────────────────────────────────────────────────────────────
 * Base:        Original CottonCubs.jsx (v1) — cream/sage/terracotta palette,
 *              Playfair + Nunito, blob hero, glassmorphism cart, analytics
 *
 * Tweaks applied:
 *  1. BRAND IDENTITY   — Cotton Cubs wreath logo SVG, Prajakta's founder story,
 *                        bio text, Instagram @cottoncubsindia, real brand voice
 *  2. CATALOG          — Products from Cotton Cubs India (jablas, frocks, coords,
 *                        rompers, bloomers) with Drive image stubs ready to swap
 *  3. LAYOUT ORG       — Liz Jacob-style: dropdown mega-nav (Girls/Boys/Baby),
 *                        full-width hero banner slider, category shop-tiles,
 *                        tabbed New Arrivals / Best Sellers product section
 * ─────────────────────────────────────────────────────────────────────────────
 * TO ADD REAL IMAGES: replace each product's `img` value with a public URL or
 * import. Drive images need to be made publicly shareable first, then use the
 * direct link format: https://drive.google.com/uc?id=FILE_ID
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
  ShoppingCart, X, Plus, Minus, Heart, Star,
  MessageCircle, ArrowRight, ChevronRight,
  Search, Instagram, ChevronDown
} from "lucide-react";
import useProducts from "./hooks/useProducts";

// ─────────────────────────────────────────────────────────────────────────────
// Analytics Service
// ─────────────────────────────────────────────────────────────────────────────
const AnalyticsService = {
  track(element_id, action_type, metadata = {}) {
    const event = { timestamp: new Date().toISOString(), element_id, action_type, ...metadata };
    console.log(
      "%c[CottonCubs Analytics] %c" + action_type,
      "background:#c9907a;color:#fff;padding:2px 6px;border-radius:4px 0 0 4px;font-weight:bold;font-size:11px",
      "background:#7c9e87;color:#fff;padding:2px 8px;border-radius:0 4px 4px 0;font-size:11px",
      "\n", event
    );
  }
};

// Catalog data is now in src/data/products.js and loaded via useProducts hook
// ─────────────────────────────────────────────────────────────────────────────
// Nav structure — mirrors Liz Jacob dropdown organisation
// ─────────────────────────────────────────────────────────────────────────────
const NAV_MENUS = {
  Girls: ["Shop All", "Dresses", "Coords & Sets", "Ethnic Wear", "Sleepwear"],
  Boys:  ["Shop All", "Shirts & Sets", "Ethnic Wear", "Sleepwear"],
  Baby:  ["Shop All", "Baby Girls", "Baby Boys", "Jabla Sets", "Onesies & Rompers"],
};

// Hero banner slides (bg gradient + headline) — swap `img` for real photos
const SLIDES = [
  { bg: "linear-gradient(120deg,#f5ede2 0%,#ece4d4 100%)", emoji: "🪻", tag: "New Collection", headline: "Pure Comfort\nfor Little Cubs", cta: "Shop Baby" },
  { bg: "linear-gradient(120deg,#fde9c8 0%,#f5dbb0 100%)", emoji: "🌼", tag: "Girls 1–6Y", headline: "Frocks that Move\nlike She Does", cta: "Shop Girls" },
  { bg: "linear-gradient(120deg,#d8ecd8 0%,#c0dcc0 100%)", emoji: "🌿", tag: "Boys 1–6Y", headline: "Easy Cotton\nfor Busy Boys", cta: "Shop Boys" },
];

// Shop-by-category tiles — same as Liz Jacob's grid below hero
const SHOP_TILES = [
  { label: "BABY",   emoji: "🍼", bg: "#fce8f0", border: "#f0c0d4", filter: "Baby" },
  { label: "GIRLS",  emoji: "🌸", bg: "#fce8f4", border: "#f0b8e0", filter: "Girls" },
  { label: "BOYS",   emoji: "🌿", bg: "#e8f5e9", border: "#b8dcc0", filter: "Boys" },
  { label: "JABLA SETS", emoji: "👶", bg: "#f5f0e8", border: "#ddd0c0", filter: "Baby" },
  { label: "COORDS", emoji: "🎀", bg: "#fef3d8", border: "#f0dca0", filter: "Girls" },
  { label: "ETHNIC", emoji: "🪔", bg: "#f8e8dc", border: "#e8c4a8", filter: "Girls" },
];

const BLOGS = [
  { id:1, emoji:"🧵", tag:"Fabric Truth", read:"4 min",
    title:"Why I'll Never Buy Polycotton for My Kids Again",
    excerpt:"Jersey, rayon, polycotton — all sold as cotton. Here's how to spot the real thing before you pay.", bg:"linear-gradient(135deg,#f5ede4,#e8d8c8)" },
  { id:2, emoji:"👗", tag:"Curation Guide", read:"6 min",
    title:"A 10-Piece Cotton Wardrobe for Ages 0–3",
    excerpt:"You don't need 30 outfits. You need 10 that breathe, wash well, and actually fit Indian weather.", bg:"linear-gradient(135deg,#e4ede8,#c8dcd0)" },
  { id:3, emoji:"🫧", tag:"Care Tips", read:"3 min",
    title:"How to Wash Cotton Kidswear Without Ruining It",
    excerpt:"Cold water, inside out, no dryer. Prajakta's exact routine for keeping colours bright wash after wash.", bg:"linear-gradient(135deg,#ede4f0,#d8c8e8)" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function useIntersection(ref, threshold = 0.25) {
  const [v, setV] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, [ref, threshold]);
  return v;
}

// Cotton Cubs wreath logo — faithful to the circular script mark
function CottonCubsLogo({ size = 44 }) {
  const r = 44;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
      <circle cx="50" cy="50" r={r} fill="white" fillOpacity=".9"/>
      <circle cx="50" cy="50" r={r} stroke="#c9907a" strokeWidth="1.2" strokeDasharray="3.5 4.5" fill="none"/>
      <circle cx="50" cy="50" r="38" stroke="#c9907a" strokeWidth=".5" fill="none" opacity=".35"/>
      {[0,45,90,135,180,225,270,315].map(deg => {
        const x = 50 + r * Math.cos((deg-90)*Math.PI/180);
        const y = 50 + r * Math.sin((deg-90)*Math.PI/180);
        return <circle key={deg} cx={x} cy={y} r="1.6" fill="#c9907a" opacity=".6"/>;
      })}
      <text x="50" y="44" textAnchor="middle" fontFamily="Georgia,serif" fontSize="12" fill="#3d2416" fontStyle="italic" fontWeight="600">Cotton</text>
      <text x="50" y="58" textAnchor="middle" fontFamily="Georgia,serif" fontSize="12" fill="#3d2416" fontStyle="italic" fontWeight="600">Cubs</text>
      <text x="50" y="68" textAnchor="middle" fontFamily="Georgia,serif" fontSize="5" fill="#7a5040" letterSpacing="2.5" opacity=".7">ORGANIC CLOTHING</text>
    </svg>
  );
}

// Product image — shows real img if provided, falls back to gradient+emoji
function ProductImage({ product, height = 220, fontSize = 88, onZoom }) {
  const [err, setErr] = useState(false);
  if (product.img && !err) {
    return (
      <div style={{ position:"relative", overflow:"hidden", cursor: onZoom ? "zoom-in" : "default" }}
        onClick={onZoom ? (e) => { e.stopPropagation(); onZoom(product); } : undefined}
        className="pimg-wrap">
        <img src={product.img} alt={product.name} onError={() => setErr(true)}
          style={{ width:"100%", height, objectFit:"cover", display:"block", transition:"transform .4s ease" }}
          className="pimg"/>
        {onZoom && (
          <div className="pimg-zoom-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
            </svg>
          </div>
        )}
      </div>
    );
  }
  return (
    <div style={{ background: product.bg, height, display:"flex", alignItems:"center", justifyContent:"center", fontSize, transition:"font-size .3s" }}>
      {product.emoji}
    </div>
  );
}

function ImageLightbox({ product, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <div className="lightbox-box" onClick={(e) => e.stopPropagation()}>
        <button className="lightbox-close btn" onClick={onClose}>
          <X size={20} color="white"/>
        </button>
        <img src={product.img} alt={product.name}
          style={{ maxWidth:"90vw", maxHeight:"80vh", objectFit:"contain", borderRadius:12, display:"block" }}/>
        <div style={{ padding:"14px 4px 4px", textAlign:"center" }}>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:4 }}>{product.name}</div>
          <div style={{ fontSize:15, color:"rgba(255,255,255,.7)" }}>₹{product.price}
            <span style={{ marginLeft:10, textDecoration:"line-through", fontSize:13, opacity:.6 }}>₹{product.mrp}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// App
// ─────────────────────────────────────────────────────────────────────────────
export default function CottonCubs() {
  const { activeProducts: PRODUCTS } = useProducts();
  const [cart, setCart]             = useState([]);
  const [cartOpen, setCartOpen]     = useState(false);
  const [lastViewed, setLastViewed] = useState(null);
  const [addedId, setAddedId]       = useState(null);
  const [wishlist, setWishlist]     = useState([]);
  const [navScrolled, setNavScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);   // eslint-disable-line no-unused-vars
  const [activeTab, setActiveTab]   = useState("New Arrivals");
  const [shopFilter, setShopFilter] = useState("All");
  const [slideIdx, setSlideIdx]     = useState(0);
  const [zoomedProduct, setZoomedProduct] = useState(null);

  const aboutRef = useRef(null);
  const blogRef  = useRef(null);
  const aTracked = useRef(false);
  const bTracked = useRef(false);
  const aboutVis = useIntersection(aboutRef);
  const blogVis  = useIntersection(blogRef);

  useEffect(() => {
    if (aboutVis && !aTracked.current) { aTracked.current = true; AnalyticsService.track("about-section","section_view"); }
  }, [aboutVis]);
  useEffect(() => {
    if (blogVis && !bTracked.current) { bTracked.current = true; AnalyticsService.track("blog-section","section_view"); }
  }, [blogVis]);
  useEffect(() => {
    const fn = () => setNavScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  // Auto-advance hero slider
  useEffect(() => {
    const t = setInterval(() => setSlideIdx(i => (i + 1) % SLIDES.length), 4000);
    return () => clearInterval(t);
  }, []);

  const addToCart = useCallback((product) => {
    setCart(prev => {
      const ex = prev.find(i => i.id === product.id);
      return ex ? prev.map(i => i.id===product.id ? {...i,qty:i.qty+1} : i)
                : [...prev, {...product, qty:1}];
    });
    setLastViewed(product);
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 1400);
    AnalyticsService.track(`product-${product.id}`, "add_to_cart", { product_name: product.name, price: `₹${product.price}` });
  }, []);

  const removeFromCart = useCallback((id, name) => {
    setCart(prev => prev.filter(i => i.id !== id));
    AnalyticsService.track(`product-${id}`, "remove_from_cart", { product_name: name });
  }, []);

  const changeQty = useCallback((id, delta, name) => {
    setCart(prev => prev.map(i => i.id===id ? {...i, qty: Math.max(1, i.qty+delta)} : i));
    AnalyticsService.track(`product-${id}`, delta>0?"qty_increase":"qty_decrease", { product_name:name });
  }, []);

  const toggleWishlist = id =>
    setWishlist(p => p.includes(id) ? p.filter(x=>x!==id) : [...p, id]);

  const handleCheckout = () => {
    AnalyticsService.track("checkout-btn","checkout_click",{
      cart_total:`₹${(total+shipping).toLocaleString()}`,
      item_count: cart.reduce((s,i)=>s+i.qty,0)
    });
    alert("Checkout coming soon! 🎉\nDM @cottoncubsindia on Instagram or WhatsApp to place your order.");
  };

  const handleWhatsApp = () => {
    AnalyticsService.track("whatsapp-btn","whatsapp_engage",{ last_viewed: lastViewed?.name });
    const msg = encodeURIComponent(`Hi Cotton Cubs! I'd like to know more about the "${lastViewed?.name || 'your collection'}" 🌸`);
    window.open(`https://wa.me/919876543210?text=${msg}`, "_blank");
  };

  const total     = cart.reduce((s,i)=>s+i.price*i.qty, 0);
  const shipping  = total>0 && total<999 ? 80 : 0;
  const itemCount = cart.reduce((s,i)=>s+i.qty, 0);

  // Tabbed product display — New Arrivals vs Best Sellers
  const tabProducts = {
    "New Arrivals":  PRODUCTS.filter(p=>["New","Popular","Limited"].includes(p.tag) || !p.tag).slice(0,4),
    "Best Sellers":  PRODUCTS.filter(p=>p.tag==="Bestseller" || p.tag==="Popular"),
    "Featured":      PRODUCTS.slice(0,4),
  };
  const shopFiltered = shopFilter==="All" ? PRODUCTS
    : PRODUCTS.filter(p => p.category===shopFilter);

  const slide = SLIDES[slideIdx];

  return (
    <div style={{fontFamily:"'Nunito',sans-serif", background:"#faf6f0", color:"#3d2b1f", minHeight:"100vh"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,600&family=Nunito:wght@300;400;500;600;700;800;900&display=swap');

        :root {
          --cream:  #faf6f0; --cream2: #f5efe4; --cream3: #ede3d5;
          --sage:   #7c9e87; --sage2:  #9ab8a3; --sage3:  #c8ddd0;
          --terra:  #c0735a; --terra2: #d4907a; --terra3: #f0d5cb;
          --brown:  #5c3d2e; --brown2: #3d2b1f; --text2:  #7a5c4a;
          --white:  #ffffff;
          --sh:  0 2px 12px rgba(92,61,46,.08);
          --shm: 0 8px 32px rgba(92,61,46,.12);
          --shl: 0 20px 60px rgba(92,61,46,.18);
          --glass-bg: rgba(250,246,240,.72);
          --glass-bd: rgba(255,255,255,.65);
        }
        *  { box-sizing:border-box; margin:0; padding:0; }
        a  { text-decoration:none; color:inherit; }
        .pf { font-family:'Playfair Display',serif; }

        /* Animations */
        @keyframes fadeUp  { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes slideR  { from{transform:translateX(105%)} to{transform:translateX(0)} }
        @keyframes pop     { 0%{transform:scale(.78);opacity:0} 65%{transform:scale(1.08)} 100%{transform:scale(1);opacity:1} }
        @keyframes float   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-9px)} }
        @keyframes blobP   { 0%,100%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%} 50%{border-radius:30% 60% 70% 40%/50% 60% 30% 60%} }
        @keyframes marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        @keyframes slideFd { from{opacity:0;transform:translateX(12px)} to{opacity:1;transform:translateX(0)} }

        .fu   { animation:fadeUp .65s ease both; }
        .fi   { animation:fadeIn .4s ease both; }
        .pop  { animation:pop .38s cubic-bezier(.34,1.56,.64,1) both; }
        .flt  { animation:float 3.5s ease-in-out infinite; }
        .blobP{ animation:blobP 8s ease-in-out infinite; }
        .d1{animation-delay:.07s;opacity:0} .d2{animation-delay:.14s;opacity:0}
        .d3{animation-delay:.21s;opacity:0} .d4{animation-delay:.28s;opacity:0}
        .d5{animation-delay:.35s;opacity:0} .d6{animation-delay:.42s;opacity:0}

        ::-webkit-scrollbar{width:5px}
        ::-webkit-scrollbar-track{background:var(--cream2)}
        ::-webkit-scrollbar-thumb{background:var(--sage2);border-radius:3px}

        /* ── Announcement bar ── */
        .ann { background:var(--terra);color:#fff;overflow:hidden;font-size:12px;font-weight:700;padding:8px 0;position:fixed;top:0;left:0;right:0;z-index:210; }
        .ann-track { display:flex;animation:marquee 24s linear infinite;width:max-content; }

        /* ── Nav ── */
        .nav { position:fixed;top:32px;left:0;right:0;z-index:200;transition:all .35s; }
        .nav-solid { background:rgba(250,246,240,.95);backdrop-filter:blur(20px);box-shadow:var(--sh); }

        /* Dropdown */
        .dd-wrap { position:relative; }
        .dd-menu { position:absolute;top:calc(100% + 12px);left:50%;transform:translateX(-50%);background:#fff;border-radius:14px;box-shadow:var(--shm);padding:16px 0;min-width:180px;opacity:0;pointer-events:none;transition:all .22s ease;transform:translateX(-50%) translateY(6px); }
        .dd-wrap:hover .dd-menu { opacity:1;pointer-events:all;transform:translateX(-50%) translateY(0); }
        .dd-item { padding:9px 20px;font-size:14px;font-weight:600;color:var(--text2);cursor:pointer;transition:background .15s; }
        .dd-item:hover { background:var(--cream2);color:var(--terra); }

        /* ── Buttons ── */
        .btn { border:none;cursor:pointer;font-family:'Nunito',sans-serif;transition:all .22s ease; }
        .btn-terra { background:var(--terra);color:#fff;border-radius:50px;padding:13px 30px;font-size:14px;font-weight:800; }
        .btn-terra:hover { background:var(--brown);transform:translateY(-2px);box-shadow:0 8px 22px rgba(192,115,90,.4); }
        .btn-ghost { background:transparent;color:var(--brown2);border:1.5px solid var(--cream3);border-radius:50px;padding:12px 28px;font-size:14px;font-weight:700; }
        .btn-ghost:hover { border-color:var(--terra);color:var(--terra); }
        .btn-sage { background:var(--sage);color:#fff;border-radius:50px;padding:12px 26px;font-size:14px;font-weight:700; }
        .btn-sage:hover { background:#5c7a65;transform:translateY(-2px); }
        .btn-icon { background:#fff;border:1.5px solid var(--cream3);border-radius:50%;width:40px;height:40px;display:flex;align-items:center;justify-content:center;box-shadow:var(--sh);cursor:pointer; }
        .btn-icon:hover { border-color:var(--terra); }

        /* ── Hero slider ── */
        .hero-slide { animation:slideFd .6s ease both; }
        .slide-dot { width:8px;height:8px;border-radius:50%;background:rgba(255,255,255,.5);cursor:pointer;transition:all .2s;border:none; }
        .slide-dot.on { background:#fff;width:22px;border-radius:4px; }

        /* ── Shop tiles (Liz Jacob style) ── */
        .shop-tile { border-radius:16px;overflow:hidden;cursor:pointer;transition:transform .28s ease,box-shadow .28s ease;box-shadow:var(--sh); }
        .shop-tile:hover { transform:translateY(-4px);box-shadow:var(--shm); }
        .shop-tile-img { display:flex;align-items:center;justify-content:center;font-size:56px;transition:transform .4s ease; }
        .shop-tile:hover .shop-tile-img { transform:scale(1.06); }
        .shop-tile-label { background:#fff;padding:10px 0;text-align:center;font-size:12px;font-weight:800;letter-spacing:.1em;color:var(--brown2); }

        /* ── Product tabs ── */
        .tab-btn { background:transparent;border:none;border-bottom:2px solid transparent;padding:10px 20px;font-family:'Nunito',sans-serif;font-size:14px;font-weight:700;cursor:pointer;color:var(--text2);transition:all .2s; }
        .tab-btn.on { color:var(--terra);border-bottom-color:var(--terra); }
        .tab-btn:hover:not(.on) { color:var(--terra); }

        /* ── Cards ── */
        .pcard { background:#fff;border-radius:22px;overflow:hidden;box-shadow:var(--sh);transition:all .3s ease; }
        .pcard:hover { transform:translateY(-5px);box-shadow:var(--shl); }
        .pcard:hover .pcard-emoji { transform:scale(1.12) rotate(-5deg); }
        .pcard-emoji { transition:transform .4s ease;display:block; }

        .bcard { background:#fff;border-radius:20px;overflow:hidden;box-shadow:var(--sh);transition:all .3s ease;cursor:pointer; }
        .bcard:hover { transform:translateY(-5px);box-shadow:var(--shm); }

        /* ── Glassmorphism cart ── */
        .cart-panel {
          position:fixed;top:0;right:0;bottom:0;width:min(420px,100vw);z-index:500;
          display:flex;flex-direction:column;
          background:var(--glass-bg);
          backdrop-filter:blur(36px) saturate(180%);
          -webkit-backdrop-filter:blur(36px) saturate(180%);
          border-left:1px solid var(--glass-bd);
          box-shadow:-16px 0 64px rgba(92,61,46,.18);
        }
        .c-item { background:rgba(255,255,255,.55);border:1px solid rgba(255,255,255,.8);border-radius:14px;backdrop-filter:blur(8px); }

        .overlay { position:fixed;inset:0;background:rgba(61,43,31,.3);backdrop-filter:blur(3px);z-index:499; }

        /* ── Tags / badges ── */
        .tag { display:inline-block;border-radius:50px;padding:3px 11px;font-size:10px;font-weight:800;letter-spacing:.05em;text-transform:uppercase; }
        .t-terra { background:var(--terra3);color:var(--terra); }
        .t-sage  { background:var(--sage3);color:#4a7358; }
        .t-gold  { background:#fef0c0;color:#a07820; }
        .cbadge  { position:absolute;top:-7px;right:-7px;background:var(--terra);color:#fff;border-radius:50%;width:19px;height:19px;font-size:10px;font-weight:900;display:flex;align-items:center;justify-content:center;animation:pop .3s ease; }

        /* ── Filter pills ── */
        .fpill { border:1.5px solid var(--cream3);border-radius:50px;padding:7px 18px;font-size:13px;font-weight:700;cursor:pointer;background:#fff;color:var(--text2);transition:all .2s; }
        .fpill.on { background:var(--terra);color:#fff;border-color:var(--terra); }
        .fpill:hover:not(.on) { border-color:var(--terra);color:var(--terra); }

        /* ── Image zoom ── */
        .pimg-wrap:hover .pimg { transform:scale(1.07); }
        .pimg-zoom-icon { position:absolute;bottom:10px;right:10px;background:rgba(0,0,0,.45);border-radius:50%;width:34px;height:34px;display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .2s;pointer-events:none; }
        .pimg-wrap:hover .pimg-zoom-icon { opacity:1; }

        /* ── Lightbox ── */
        @keyframes lbIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
        .lightbox-overlay { position:fixed;inset:0;background:rgba(0,0,0,.82);backdrop-filter:blur(6px);z-index:600;display:flex;align-items:center;justify-content:center;padding:20px; }
        .lightbox-box { position:relative;animation:lbIn .22s ease both; }
        .lightbox-close { position:absolute;top:-14px;right:-14px;background:rgba(255,255,255,.15);border:none;border-radius:50%;width:38px;height:38px;display:flex;align-items:center;justify-content:center;cursor:pointer;z-index:1;transition:background .2s; }
        .lightbox-close:hover { background:rgba(255,255,255,.3); }

        /* ── WhatsApp ── */
        .wa { position:fixed;bottom:28px;right:28px;z-index:400;width:56px;height:56px;border-radius:50%;background:linear-gradient(135deg,#25D366,#128C7E);border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 6px 28px rgba(37,211,102,.4);animation:float 3.5s ease-in-out infinite; }
        .wa:hover { transform:scale(1.12)!important;box-shadow:0 14px 44px rgba(37,211,102,.5)!important; }
        .wa-tip { position:absolute;right:64px;background:#fff;color:var(--brown2);font-size:12px;font-weight:700;padding:6px 12px;border-radius:8px;white-space:nowrap;box-shadow:var(--sh);opacity:0;transition:opacity .2s;pointer-events:none;max-width:200px;overflow:hidden;text-overflow:ellipsis; }
        .wa:hover .wa-tip { opacity:1; }

        /* Responsive */
        @media(max-width:860px){
          .g2{grid-template-columns:1fr!important}
          .g3{grid-template-columns:repeat(2,1fr)!important}
          .g4{grid-template-columns:repeat(2,1fr)!important}
          .g6{grid-template-columns:repeat(3,1fr)!important}
          .hide-sm{display:none!important}
        }
        @media(max-width:520px){
          .g3{grid-template-columns:1fr!important}
          .g6{grid-template-columns:repeat(2,1fr)!important}
        }
      `}</style>

      {/* ── Announcement Marquee ── */}
      <div className="ann">
        <div className="ann-track">
          {[0,1].map(k=>(
            <span key={k} style={{display:"flex",gap:40,paddingRight:40,alignItems:"center",whiteSpace:"nowrap"}}>
              {["🌿 100% Pure Cotton — Always","🚚 Ships across India","🌎 100% Eco-friendly","💌 DM for orders · @cottoncubsindia","👶 Sizes 0–6 Years","✨ Gets softer with every wash"].map(t=>(
                <span key={t}>{t}</span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* ── Navbar — Liz Jacob style with dropdowns ── */}
      <nav className={`nav ${navScrolled?"nav-solid":""}`} style={{padding:"0 28px"}}>
        <div style={{maxWidth:1200,margin:"0 auto",display:"flex",alignItems:"stretch",justifyContent:"space-between",height:68}}>
          
          {/* Logo */}
          <div style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}
            onClick={()=>window.scrollTo({top:0,behavior:"smooth"})}>
            <CottonCubsLogo size={55}/>
            <div>
              <div className="pf" style={{fontSize:21,fontWeight:700,color:"var(--brown2)",lineHeight:1.1,fontStyle:"italic"}}>Cotton Cubs</div>
              <div style={{fontSize:8,letterSpacing:".18em",color:"var(--text2)",textTransform:"uppercase",fontWeight:700}}>Organic Clothing</div>
            </div>
          </div>

          {/* Centre nav links with dropdowns */}
          <div className="hide-sm" style={{display:"flex",alignItems:"stretch",gap:4}}>
            <button type="button" onClick={()=>window.scrollTo({top:0,behavior:"smooth"})} style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",padding:"0 16px",fontSize:14,fontWeight:700,fontFamily:"inherit",color:"var(--text2)",transition:"color .2s"}}
              onMouseEnter={e=>e.currentTarget.style.color="var(--terra)"}
              onMouseLeave={e=>e.currentTarget.style.color="var(--text2)"}>Home</button>

            {Object.entries(NAV_MENUS).map(([label, items])=>(
              <div key={label} className="dd-wrap" style={{display:"flex",alignItems:"center"}}>
                <div style={{display:"flex",alignItems:"center",gap:4,padding:"0 14px",fontSize:14,fontWeight:700,color:"var(--text2)",cursor:"pointer",height:"100%",transition:"color .2s"}}
                  onMouseEnter={e=>e.currentTarget.style.color="var(--terra)"}
                  onMouseLeave={e=>e.currentTarget.style.color="var(--text2)"}>
                  {label} <ChevronDown size={13}/>
                </div>
                <div className="dd-menu">
                  {items.map(item=>(
                    <div key={item} className="dd-item"
                      onClick={()=>{setShopFilter(label);document.getElementById("shop")?.scrollIntoView({behavior:"smooth"});}}>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <a href="#about" style={{display:"flex",alignItems:"center",padding:"0 14px",fontSize:14,fontWeight:700,color:"var(--text2)",transition:"color .2s"}}
              onMouseEnter={e=>e.currentTarget.style.color="var(--terra)"}
              onMouseLeave={e=>e.currentTarget.style.color="var(--text2)"}>About Us</a>
            <a href="#blog" style={{display:"flex",alignItems:"center",padding:"0 14px",fontSize:14,fontWeight:700,color:"var(--text2)",transition:"color .2s"}}
              onMouseEnter={e=>e.currentTarget.style.color="var(--terra)"}
              onMouseLeave={e=>e.currentTarget.style.color="var(--text2)"}>Blog</a>
          </div>

          {/* Right icons */}
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <a href="https://instagram.com/cottoncubsindia" target="_blank" rel="noreferrer"
              className="btn-icon hide-sm btn"><Instagram size={17} color="var(--brown2)"/></a>
            <button className="btn-icon btn"><Search size={17} color="var(--brown2)"/></button>
            <button className="btn-icon btn" style={{position:"relative"}} onClick={()=>setCartOpen(true)}>
              <ShoppingCart size={17} color="var(--brown2)"/>
              {itemCount>0 && <span className="cbadge">{itemCount}</span>}
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero Banner Slider — full-width, Liz Jacob style ── */}
      <section style={{paddingTop:120,background:slide.bg,minHeight:"min(90vh,680px)",display:"flex",alignItems:"center",position:"relative",overflow:"hidden",transition:"background .6s ease"}}>
        {/* Decorative blob */}
        <div className="blobP" style={{position:"absolute",right:"-8%",top:"10%",width:480,height:480,background:"rgba(255,255,255,.18)",borderRadius:"60% 40% 30% 70%/60% 30% 70% 40%",pointerEvents:"none"}}/>

        <div style={{maxWidth:1200,margin:"0 auto",padding:"0 28px",width:"100%"}}>
          <div style={{display:"grid",gridTemplateColumns:"1.1fr 0.9fr",gap:60,alignItems:"center"}} className="g2">
            <div className="hero-slide" key={slideIdx}>
              <span className="tag t-terra fu" style={{marginBottom:20,display:"inline-block"}}>{slide.tag}</span>
              <h1 className="pf fu d1" style={{fontSize:"clamp(44px,5.5vw,72px)",lineHeight:1.15,color:"var(--brown2)",marginBottom:22,fontWeight:700,whiteSpace:"pre-line"}}>
                {slide.headline.split("\n")[0]}<br/>
                <em style={{color:"var(--terra)",fontStyle:"italic"}}>{slide.headline.split("\n")[1]}</em>
              </h1>
              <p className="fu d2" style={{fontSize:16,color:"var(--text2)",lineHeight:1.85,marginBottom:14,maxWidth:460}}>
                Hi, I'm <strong>Prajakta</strong> — mother of two beautiful daughters, and the face behind Cotton Cubs.
              </p>
              <p className="fu d2" style={{fontSize:16,color:"var(--text2)",lineHeight:1.85,marginBottom:34,maxWidth:460}}>
                Kids cotton wear 🤍 0–6 years · Ships across India 🌎 · 100% eco-friendly · DM for orders
              </p>
              <div className="fu d3" style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:36}}>
                <button className="btn btn-terra" onClick={()=>document.getElementById("shop")?.scrollIntoView({behavior:"smooth"})}>
                  {slide.cta} <ArrowRight size={14} style={{display:"inline",marginLeft:6,verticalAlign:"middle"}}/>
                </button>
                <button className="btn btn-ghost" onClick={()=>document.getElementById("about")?.scrollIntoView({behavior:"smooth"})}>
                  Our Story
                </button>
              </div>
              {/* Trust pills */}
              <div className="fu d4" style={{display:"flex",gap:14,flexWrap:"wrap"}}>
                {[["🌿","100% Pure Cotton"],["🌎","Ships Pan-India"],["🧺","Softer with every wash"]].map(([e,t])=>(
                  <div key={t} style={{display:"flex",alignItems:"center",gap:6,background:"rgba(255,255,255,.7)",borderRadius:50,padding:"6px 14px",fontSize:13,fontWeight:700,color:"var(--text2)",boxShadow:"var(--sh)"}}>
                    <span>{e}</span><span>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Product preview card */}
            <div className="fu d2 flt" style={{display:"flex",justifyContent:"center"}}>
              <div style={{background:"#fff",borderRadius:28,overflow:"hidden",boxShadow:"var(--shl)",width:300,position:"relative"}}>
                <ProductImage product={PRODUCTS[slideIdx % PRODUCTS.length]} height={230} fontSize={92}/>
                <div style={{padding:"18px 20px 22px"}}>
                  <div style={{fontSize:11,fontWeight:800,color:"var(--text2)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:3}}>{PRODUCTS[slideIdx%PRODUCTS.length].subtitle}</div>
                  <div style={{fontWeight:800,fontSize:16,color:"var(--brown2)",marginBottom:6}}>{PRODUCTS[slideIdx%PRODUCTS.length].name}</div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontWeight:900,fontSize:18,color:"var(--terra)"}}>₹{PRODUCTS[slideIdx%PRODUCTS.length].price}</span>
                    <span style={{fontSize:13,color:"#bbb",textDecoration:"line-through"}}>₹{PRODUCTS[slideIdx%PRODUCTS.length].mrp}</span>
                  </div>
                </div>
                {PRODUCTS[slideIdx%PRODUCTS.length].tag && (
                  <div style={{position:"absolute",top:14,left:0,background:"var(--terra)",color:"#fff",fontSize:10,fontWeight:900,borderRadius:"0 8px 8px 0",padding:"4px 12px",letterSpacing:".05em"}}>
                    {PRODUCTS[slideIdx%PRODUCTS.length].tag.toUpperCase()}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Slide dots */}
          <div style={{display:"flex",gap:8,justifyContent:"center",marginTop:40,paddingBottom:32}}>
            {SLIDES.map((_,i)=>(
              <button key={i} className={`slide-dot btn ${slideIdx===i?"on":""}`} onClick={()=>setSlideIdx(i)}/>
            ))}
          </div>
        </div>
      </section>

      {/* ── Shop by Category tiles — Liz Jacob grid layout ── */}
      <section style={{padding:"56px 28px 44px",background:"#fff"}}>
        <div style={{maxWidth:1200,margin:"0 auto"}}>
          <h2 className="pf" style={{fontSize:32,color:"var(--brown2)",fontWeight:700,textAlign:"center",marginBottom:32}}>Shop the Collection</h2>
          <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:14}} className="g6">
            {SHOP_TILES.map((t,i)=>(
              <div key={t.label} className={`shop-tile fu d${i+1}`}
                style={{}}
                onClick={()=>{setShopFilter(t.filter);document.getElementById("shop")?.scrollIntoView({behavior:"smooth"});}}>
                <div className="shop-tile-img" style={{background:t.bg,border:`1.5px solid ${t.border}`,aspectRatio:"1",borderBottom:"none",borderRadius:"16px 16px 0 0"}}>{t.emoji}</div>
                <div className="shop-tile-label" style={{border:`1.5px solid ${t.border}`,borderTop:"none",borderRadius:"0 0 16px 16px"}}>{t.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Tabbed Product Section — New Arrivals / Best Sellers / Featured ── */}
      <section style={{padding:"16px 28px 72px",background:"var(--cream2)"}}>
        <div style={{maxWidth:1200,margin:"0 auto"}}>
          {/* Tab buttons */}
          <div style={{display:"flex",gap:0,borderBottom:"1px solid var(--cream3)",marginBottom:40,justifyContent:"center"}}>
            {["New Arrivals","Best Sellers","Featured"].map(tab=>(
              <button key={tab} className={`tab-btn btn ${activeTab===tab?"on":""}`} onClick={()=>setActiveTab(tab)}>{tab}</button>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:24}} className="g4">
            {(tabProducts[activeTab]??PRODUCTS.slice(0,4)).map((p,i)=>(
              <div key={p.id} className={`pcard fu d${i+1}`} onMouseEnter={()=>setLastViewed(p)}>
                <div style={{position:"relative",overflow:"hidden"}}>
                  <ProductImage product={p} height={210} fontSize={84} onZoom={p.img ? setZoomedProduct : null}/>
                  {p.tag && <span style={{position:"absolute",top:12,left:12,background:"#fff",borderRadius:50,padding:"3px 10px",fontSize:10,fontWeight:900,color:p.accent,boxShadow:"var(--sh)"}}>{p.tag}</span>}
                  <button onClick={()=>toggleWishlist(p.id)} className="btn"
                    style={{position:"absolute",top:12,right:12,background:"#fff",borderRadius:"50%",width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"var(--sh)"}}>
                    <Heart size={14} fill={wishlist.includes(p.id)?"#e07060":"none"} color={wishlist.includes(p.id)?"#e07060":"#aaa"}/>
                  </button>
                </div>
                <div style={{padding:"16px 18px 20px"}}>
                  <div style={{fontSize:10,fontWeight:800,color:"var(--text2)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:3}}>{p.subtitle}</div>
                  <div style={{fontWeight:800,fontSize:16,color:"var(--brown2)",marginBottom:6}}>{p.name}</div>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                    <span style={{fontWeight:900,fontSize:17,color:"var(--terra)"}}>₹{p.price}</span>
                    <span style={{fontSize:12,color:"#bbb",textDecoration:"line-through"}}>₹{p.mrp}</span>
                    <span style={{background:"var(--sage3)",color:"#4a7358",borderRadius:50,padding:"2px 7px",fontSize:10,fontWeight:800}}>
                      {Math.round((1-p.price/p.mrp)*100)}% off
                    </span>
                  </div>
                  <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:14}}>
                    {p.sizes.map(s=>(
                      <span key={s} style={{background:"var(--cream2)",borderRadius:7,padding:"2px 7px",fontSize:10,fontWeight:800,color:"var(--text2)",border:"1px solid var(--cream3)"}}>{s}</span>
                    ))}
                  </div>
                  <button className="btn btn-terra" style={{width:"100%",padding:"11px",fontSize:13,background:addedId===p.id?"var(--sage)":"var(--terra)"}}
                    onClick={()=>addToCart(p)}>
                    {addedId===p.id?"✓ Added!":"Add to Cart"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Full Shop with filter ── */}
      <section id="shop" style={{padding:"64px 28px 80px",background:"#fff"}}>
        <div style={{maxWidth:1200,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:14}}>
            <span className="tag t-sage" style={{marginBottom:12,display:"inline-block"}}>All Products</span>
            <h2 className="pf" style={{fontSize:"clamp(28px,4vw,46px)",color:"var(--brown2)",fontWeight:700,marginBottom:10}}>
              Every piece, <em style={{color:"var(--terra)"}}>pure cotton</em>
            </h2>
            <p style={{color:"var(--text2)",fontSize:15,maxWidth:420,margin:"0 auto 28px"}}>
              Only clothes I'd put on Ruhi and Pakhi. Every fabric verified. No compromises.
            </p>
          </div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap",justifyContent:"center",marginBottom:36}}>
            {["All","Baby","Girls","Boys"].map(f=>(
              <button key={f} className={`fpill btn ${shopFilter===f?"on":""}`} onClick={()=>setShopFilter(f)}>{f}</button>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:26}} className="g3">
            {shopFiltered.map((p,i)=>(
              <div key={p.id} className={`pcard fu d${(i%6)+1}`} onMouseEnter={()=>setLastViewed(p)}>
                <div style={{position:"relative",overflow:"hidden"}}>
                  <ProductImage product={p} height={230} fontSize={92} onZoom={p.img ? setZoomedProduct : null}/>
                  {p.tag && <span style={{position:"absolute",top:14,left:14,background:"#fff",borderRadius:50,padding:"3px 10px",fontSize:10,fontWeight:900,color:p.accent,boxShadow:"var(--sh)"}}>{p.tag}</span>}
                  <span style={{position:"absolute",top:14,right:14,background:"rgba(255,255,255,.8)",borderRadius:8,padding:"3px 8px",fontSize:10,fontWeight:800,color:"var(--text2)"}}>{p.sub}</span>
                  <button onClick={()=>toggleWishlist(p.id)} className="btn"
                    style={{position:"absolute",bottom:14,right:14,background:"#fff",borderRadius:"50%",width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"var(--sh)"}}>
                    <Heart size={15} fill={wishlist.includes(p.id)?"#e07060":"none"} color={wishlist.includes(p.id)?"#e07060":"#aaa"}/>
                  </button>
                </div>
                <div style={{padding:"18px 20px 22px"}}>
                  <div style={{fontSize:10,fontWeight:800,color:"var(--text2)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:3}}>{p.subtitle}</div>
                  <div style={{fontWeight:800,fontSize:17,color:"var(--brown2)",marginBottom:8}}>{p.name}</div>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:9}}>
                    <span style={{fontWeight:900,fontSize:18,color:"var(--terra)"}}>₹{p.price}</span>
                    <span style={{fontSize:13,color:"#bbb",textDecoration:"line-through"}}>₹{p.mrp}</span>
                    <span style={{background:"var(--sage3)",color:"#4a7358",borderRadius:50,padding:"2px 8px",fontSize:10,fontWeight:800}}>
                      {Math.round((1-p.price/p.mrp)*100)}% off
                    </span>
                  </div>
                  <p style={{fontSize:13,color:"var(--text2)",lineHeight:1.72,marginBottom:12}}>{p.desc}</p>
                  <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:16}}>
                    {p.sizes.map(s=>(
                      <span key={s} style={{background:"var(--cream2)",borderRadius:7,padding:"3px 8px",fontSize:11,fontWeight:700,color:"var(--text2)",border:"1px solid var(--cream3)"}}>{s}</span>
                    ))}
                  </div>
                  <button className="btn btn-terra" style={{width:"100%",padding:"12px",fontSize:14,background:addedId===p.id?"var(--sage)":"var(--terra)"}}
                    onClick={()=>addToCart(p)}>
                    {addedId===p.id?"✓ Added to Cart!":"Add to Cart"}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p style={{textAlign:"center",marginTop:38,color:"var(--text2)",fontSize:14}}>
            More prints & styles available · DM <strong style={{color:"var(--terra)"}}>@cottoncubsindia</strong> for the full catalog
          </p>
        </div>
      </section>

      {/* ── About ── */}
      <section id="about" ref={aboutRef} style={{padding:"80px 28px",background:"var(--cream2)"}}>
        <div style={{maxWidth:1200,margin:"0 auto",display:"grid",gridTemplateColumns:"1fr 1fr",gap:64,alignItems:"start"}} className="g2">
          {/* Story cards */}
          <div style={{display:"flex",flexDirection:"column",gap:20}}>
            <div style={{background:"#fff",borderRadius:22,padding:"28px",boxShadow:"var(--sh)",borderLeft:"4px solid var(--terra)"}}>
              <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:16}}>
                <div style={{width:52,height:52,background:"var(--cream2)",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>👩‍💻</div>
                <div>
                  <div style={{fontWeight:800,fontSize:16,color:"var(--brown2)"}}>Hi, I'm Prajakta 👋</div>
                  <div style={{fontSize:13,color:"var(--text2)",marginTop:2}}>Founder · Mom of Ruhi (3) & Pakhi (1) · Tech Editor</div>
                </div>
              </div>
              <p style={{fontSize:15,color:"var(--text2)",lineHeight:1.85}}>
                "After my second baby, I couldn't go to stores. And when I ordered online, 'cotton' clothes arrived in jersey, rayon, polycotton — everything except actual cotton. <em>Everything was being sold as cotton except cotton.</em>"
              </p>
            </div>
            <div style={{background:"linear-gradient(135deg,#fdf5ee,#f5e8d8)",borderRadius:22,padding:"28px",boxShadow:"var(--sh)"}}>
              <div style={{fontSize:28,marginBottom:12}}>💡</div>
              <h3 className="pf" style={{fontSize:22,fontWeight:700,color:"var(--brown2)",marginBottom:10}}>The Idea Behind Cotton Cubs</h3>
              <p style={{fontSize:15,color:"var(--text2)",lineHeight:1.85}}>
                A one-stop shop where parents can shop blindly — knowing everything they receive is only breathable, pure cotton clothing for their little ones, curated very carefully. Our store features clothing from trusted Indian manufacturers, some established as early as 1980. Plenty of colors and prints to choose from!
              </p>
            </div>
          </div>

          <div>
            <span className="tag t-terra" style={{marginBottom:18,display:"inline-block"}}>Our Promise</span>
            <h2 className="pf" style={{fontSize:"clamp(26px,3.5vw,42px)",color:"var(--brown2)",marginBottom:28,lineHeight:1.28,fontWeight:700}}>
              A store where you shop <em style={{color:"var(--terra)"}}>blindly</em> — and trust every piece
            </h2>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:32}}>
              {[
                {emoji:"🌿",t:"Only Pure Cotton",s:"No polycotton, no rayon, ever"},
                {emoji:"🌎",t:"Eco Packaging",s:"100% eco-friendly"},
                {emoji:"🚚",t:"Ships Pan-India",s:"Shipping extra"},
                {emoji:"🤱",t:"Mom-Curated",s:"Tested on Ruhi & Pakhi"},
              ].map(f=>(
                <div key={f.t} style={{background:"#fff",borderRadius:14,padding:"14px 16px",display:"flex",alignItems:"flex-start",gap:12,boxShadow:"var(--sh)"}}>
                  <span style={{fontSize:22}}>{f.emoji}</span>
                  <div>
                    <div style={{fontWeight:800,fontSize:14,color:"var(--brown2)"}}>{f.t}</div>
                    <div style={{fontSize:12,color:"var(--text2)",marginTop:2}}>{f.s}</div>
                  </div>
                </div>
              ))}
            </div>
            <a href="https://instagram.com/cottoncubsindia" target="_blank" rel="noreferrer"
              style={{display:"inline-flex",alignItems:"center",gap:10,background:"linear-gradient(135deg,#f09040,#e04080,#8040c0)",color:"#fff",borderRadius:14,padding:"14px 22px",fontWeight:800,fontSize:14,boxShadow:"0 6px 24px rgba(200,80,80,.28)"}}>
              <Instagram size={17}/> Follow @cottoncubsindia
            </a>
            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:16}}>
              {[1,2,3,4,5].map(s=><Star key={s} size={14} fill="#f59e0b" color="#f59e0b"/>)}
              <span style={{fontSize:14,color:"var(--text2)",fontWeight:700,marginLeft:4}}>Loved by 459+ Indian mamas</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Blog ── */}
      <section id="blog" ref={blogRef} style={{padding:"80px 28px",background:"#fff"}}>
        <div style={{maxWidth:1200,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:48}}>
            <span className="tag t-sage" style={{marginBottom:12,display:"inline-block"}}>From Prajakta's Desk</span>
            <h2 className="pf" style={{fontSize:"clamp(26px,3.5vw,42px)",color:"var(--brown2)",fontWeight:700}}>
              Cotton <em style={{color:"var(--terra)"}}>Talk</em>
            </h2>
            <p style={{color:"var(--text2)",marginTop:10,fontSize:15}}>Honest notes from a mom who has done all the fabric research so you don't have to.</p>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:26}} className="g3">
            {BLOGS.map((b,i)=>(
              <div key={b.id} className={`bcard fu d${i+1}`}>
                <div style={{background:b.bg,height:140,display:"flex",alignItems:"center",justifyContent:"center",fontSize:62}}>{b.emoji}</div>
                <div style={{padding:"22px 22px 26px"}}>
                  <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:12}}>
                    <span className={`tag ${i===0?"t-terra":i===1?"t-sage":"t-gold"}`}>{b.tag}</span>
                    <span style={{fontSize:12,color:"var(--text2)",fontWeight:600}}>{b.read} read</span>
                  </div>
                  <h3 style={{fontWeight:800,fontSize:16,color:"var(--brown2)",lineHeight:1.55,marginBottom:10}}>{b.title}</h3>
                  <p style={{fontSize:13,color:"var(--text2)",lineHeight:1.75,marginBottom:16}}>{b.excerpt}</p>
                  <span style={{display:"inline-flex",alignItems:"center",gap:5,color:"var(--terra)",fontWeight:800,fontSize:13}}>
                    Read more <ChevronRight size={14}/>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{background:"var(--brown2)",color:"rgba(255,255,255,.78)",padding:"52px 28px 28px"}}>
        <div style={{maxWidth:1200,margin:"0 auto"}}>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:40,marginBottom:40}} className="g2">
            <div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
                <CottonCubsLogo size={44}/>
                <div>
                  <div className="pf" style={{fontSize:19,fontWeight:700,color:"#fff",fontStyle:"italic"}}>Cotton Cubs</div>
                  <div style={{fontSize:8,letterSpacing:".18em",color:"rgba(255,255,255,.4)",textTransform:"uppercase"}}>Organic Clothing</div>
                </div>
              </div>
              <p style={{fontSize:14,lineHeight:1.85,maxWidth:280,marginBottom:20}}>
                Kids cotton wear · 0–6 years<br/>
                Shipping across India · DM for orders<br/>
                100% eco-friendly packaging
              </p>
              <a href="https://instagram.com/cottoncubsindia" target="_blank" rel="noreferrer"
                style={{display:"inline-flex",alignItems:"center",gap:8,background:"rgba(255,255,255,.12)",borderRadius:50,padding:"8px 16px",fontSize:13,fontWeight:700,color:"#fff"}}>
                <Instagram size={15}/> @cottoncubsindia
              </a>
            </div>
            {[
              {title:"Shop",links:["Baby Girls","Baby Boys","Girls 1–6Y","Boys 1–6Y","Jabla Sets"]},
              {title:"Help",links:["Shipping Policy","Size Guide","Returns","FAQ","Contact"]},
              {title:"About",links:["Our Story","Sustainability","Blog","Instagram","DM to Order"]},
            ].map(col=>(
              <div key={col.title}>
                <h4 style={{color:"#fff",fontWeight:800,marginBottom:16,fontSize:13,textTransform:"uppercase",letterSpacing:".08em"}}>{col.title}</h4>
                {col.links.map(l=>(
                  <div key={l} style={{fontSize:14,marginBottom:10,cursor:"pointer",opacity:.62,transition:"opacity .2s"}}
                    onMouseEnter={e=>e.target.style.opacity=1}
                    onMouseLeave={e=>e.target.style.opacity=.62}>{l}</div>
                ))}
              </div>
            ))}
          </div>
          <div style={{borderTop:"1px solid rgba(255,255,255,.12)",paddingTop:20,display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
            <p style={{fontSize:12,opacity:.45}}>© 2025 Cotton Cubs · A Prajakta Patil venture · India</p>
            <p style={{fontSize:12,opacity:.45}}>Made with 💛 for parents who care about fabric</p>
          </div>
        </div>
      </footer>

      {/* ── Cart Drawer — Glassmorphism ── */}
      {cartOpen && <>
        <div className="overlay fi" onClick={()=>setCartOpen(false)}/>
        <div className="cart-panel" style={{animation:"slideR .36s cubic-bezier(.34,1.2,.64,1)"}}>
          <div style={{padding:"22px 22px 18px",borderBottom:"1px solid rgba(255,255,255,.55)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <h2 className="pf" style={{fontWeight:700,fontSize:22,color:"var(--brown2)",fontStyle:"italic"}}>Your Cart 🧸</h2>
              <p style={{fontSize:13,color:"var(--text2)",marginTop:2}}>{itemCount} item{itemCount!==1?"s":""}</p>
            </div>
            <button onClick={()=>setCartOpen(false)} className="btn"
              style={{background:"rgba(255,255,255,.65)",border:"none",borderRadius:"50%",width:38,height:38,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <X size={16} color="var(--brown2)"/>
            </button>
          </div>

          <div style={{flex:1,overflowY:"auto",padding:"14px 18px",display:"flex",flexDirection:"column",gap:10}}>
            {cart.length===0 ? (
              <div style={{textAlign:"center",paddingTop:72}}>
                <div style={{fontSize:56,marginBottom:16}}>🧺</div>
                <p style={{fontWeight:800,color:"var(--brown2)",fontSize:17}}>Your cart is empty</p>
                <p style={{color:"var(--text2)",fontSize:14,marginTop:6}}>Add some cotton comfort!</p>
                <button className="btn btn-terra" style={{marginTop:22}} onClick={()=>setCartOpen(false)}>Browse Collection</button>
              </div>
            ) : cart.map(item=>(
              <div key={item.id} className="c-item" style={{padding:"13px",display:"flex",gap:13,alignItems:"center"}}>
                <div style={{width:56,height:56,background:item.bg,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,flexShrink:0,overflow:"hidden"}}>
                  {item.img ? <img src={item.img} alt={item.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/> : item.emoji}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:800,fontSize:14,color:"var(--brown2)",lineHeight:1.3}}>{item.name}</div>
                  <div style={{fontSize:12,color:"var(--text2)",marginTop:1}}>{item.color}</div>
                  <div style={{fontWeight:900,color:"var(--terra)",marginTop:3,fontSize:14}}>₹{(item.price*item.qty).toLocaleString()}</div>
                </div>
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>
                  <div style={{display:"flex",alignItems:"center",gap:7}}>
                    <button className="btn" onClick={()=>changeQty(item.id,-1,item.name)}
                      style={{background:"rgba(255,255,255,.7)",border:"none",borderRadius:"50%",width:26,height:26,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <Minus size={11} color="var(--brown2)"/>
                    </button>
                    <span style={{fontWeight:900,fontSize:14,minWidth:18,textAlign:"center"}}>{item.qty}</span>
                    <button className="btn" onClick={()=>changeQty(item.id,1,item.name)}
                      style={{background:"rgba(255,255,255,.7)",border:"none",borderRadius:"50%",width:26,height:26,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <Plus size={11} color="var(--brown2)"/>
                    </button>
                  </div>
                  <button className="btn" onClick={()=>removeFromCart(item.id,item.name)}
                    style={{background:"none",border:"none",fontSize:11,color:"#c08080",fontWeight:700}}>Remove</button>
                </div>
              </div>
            ))}
          </div>

          {cart.length>0 && (
            <div style={{padding:"16px 18px 22px",borderTop:"1px solid rgba(255,255,255,.55)",background:"rgba(255,255,255,.28)"}}>
              {shipping>0 && (
                <div style={{background:"#fff3e8",borderRadius:10,padding:"8px 14px",marginBottom:12,fontSize:13,color:"#c08040",fontWeight:700}}>
                  🚚 Add ₹{999-total} more for free shipping!
                </div>
              )}
              {shipping===0&&total>0 && <div style={{fontSize:12,color:"var(--sage)",fontWeight:800,marginBottom:10}}>✓ Free shipping applied!</div>}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <span style={{color:"var(--text2)",fontWeight:700,fontSize:14}}>Subtotal</span>
                <span style={{fontWeight:900,fontSize:19,color:"var(--brown2)"}}>₹{total.toLocaleString()}</span>
              </div>
              {shipping>0 && (
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"var(--text2)",marginBottom:4}}>
                  <span>Shipping</span><span>₹{shipping}</span>
                </div>
              )}
              <button className="btn btn-terra" style={{width:"100%",padding:"14px",fontSize:15,marginTop:14}} onClick={handleCheckout}>
                Place Order · ₹{(total+shipping).toLocaleString()}
              </button>
              <button className="btn btn-ghost" style={{width:"100%",marginTop:10}} onClick={()=>setCartOpen(false)}>
                Continue Shopping
              </button>
              <p style={{textAlign:"center",fontSize:12,color:"var(--text2)",marginTop:12}}>
                Or DM <strong style={{color:"var(--terra)"}}>@cottoncubsindia</strong> on Instagram
              </p>
            </div>
          )}
        </div>
      </>}

      {/* ── WhatsApp FAB ── */}
      <button className="wa btn" onClick={handleWhatsApp}>
        <span className="wa-tip">Ask about: {lastViewed?.name || 'our collection'}</span>
        <MessageCircle size={25} color="white" fill="white"/>
      </button>

      {/* ── Image Lightbox ── */}
      {zoomedProduct && <ImageLightbox product={zoomedProduct} onClose={() => setZoomedProduct(null)} />}
    </div>
  );
}
